/**
 * Utility to defer script loading until user interaction or browser idle
 * This improves Time to Interactive (TTI) and Total Blocking Time (TBT)
 * by deferring non-critical third-party scripts.
 *
 * The script will load on whichever happens first:
 * 1. User interaction (mouse move, touch, scroll, or keypress)
 * 2. Browser becomes idle (via requestIdleCallback with timeout)
 *
 * @param {Function} callback - Function to execute when triggered
 * @param {Object} options - Configuration options
 * @param {number} options.timeout - Max time to wait before loading (default: 3000ms)
 */
export const loadOnInteractionOrIdle = (callback, options = {}) => {
    const { timeout = 3000 } = options

    // Don't run on server
    if (typeof window === 'undefined') return

    let loaded = false
    const events = ['mousemove', 'touchstart', 'scroll', 'keydown']

    const load = () => {
        if (loaded) return
        loaded = true

        // Remove all event listeners after triggering
        events.forEach((event) => {
            window.removeEventListener(event, load, { passive: true })
        })

        // Execute the callback
        callback()
    }

    // Load on first user interaction
    events.forEach((event) => {
        window.addEventListener(event, load, { passive: true, once: true })
    })

    // Or load when idle (with timeout fallback)
    if ('requestIdleCallback' in window) {
        window.requestIdleCallback(load, { timeout })
    } else {
        // Fallback for Safari and older browsers
        setTimeout(load, Math.min(timeout, 2000))
    }

    // Return cleanup function
    return () => {
        events.forEach((event) => {
            window.removeEventListener(event, load, { passive: true })
        })
    }
}
